import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ChevronRight, ChevronLeft, SkipForward, CheckCircle } from 'lucide-react';
import { useOnboarding } from '@/hooks/useOnboarding';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export function OnboardingModal({ open, onClose }: OnboardingModalProps) {
  const { currentStep, steps, completeStep, skipOnboarding } = useOnboarding();
  const [localStep, setLocalStep] = useState(0);

  const currentStepData = steps[localStep];
  const progress = ((localStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStepData) {
      completeStep(currentStepData.id);
    }
    
    if (localStep < steps.length - 1) {
      setLocalStep(localStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (localStep > 0) {
      setLocalStep(localStep - 1);
    }
  };

  const handleSkip = async () => {
    await skipOnboarding();
    onClose();
  };

  if (!currentStepData) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            Getting Started
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {localStep + 1} of {steps.length}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{currentStepData.title}</h3>
            <p className="text-muted-foreground">{currentStepData.description}</p>
          </div>

          <div className="flex justify-between">
            <div>
              <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                <SkipForward className="h-4 w-4 mr-2" />
                Skip Tour
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={localStep === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              <Button onClick={handleNext}>
                {localStep === steps.length - 1 ? 'Complete' : 'Next'}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}